/**
 * De API-laag van werkplek v6 (sessie 67), op één plek.
 *
 * Twee soorten functies:
 *
 * 1. **Bestaande endpoints**, doorgegeven uit `lib/werkplek-api.ts` en
 *    `lib/api.ts`. Versturen, afronden, notitie, bellen, concept,
 *    klantpagina, koppeling, instellingen en de copiloot lopen via
 *    dezelfde motor als werkplek v4; v6 bouwt daar geen tweede weg naast.
 * 2. **De v6-endpoints** (`app/api/werkplek_v6.py`): de lijst met drie
 *    mappen en open zaken per kanaal, het gesprek van één zaak over alle
 *    kanalen heen met de stand van het antwoordvak, de controles voor de
 *    copiloot, antwoorden per mail op een chat- of telefoonzaak, eigen
 *    bijlagen en documenten uit het portaal, en de uitkomst van een
 *    terugbelverzoek.
 *
 * Schermcode in `pages/WerkplekV6/` importeert alleen hieruit, nooit
 * rechtstreeks uit de v4-componenten.
 */
import {
  apiClient,
  adminChatApi,
  emailsApi,
  huisdierenApi,
  whatsappTemplatesApi,
} from "@/lib/api";
import { huidigAccessToken } from "@/lib/auth-store";
import {
  afrondenApi,
  kaartenApi,
  werkplekApi,
  werkplekKlantenApi,
  werkplekKoppelingApi,
  werkplekTegelApi,
  type AfrondUitkomst,
  type KoppelBanner,
  type TegelBijlage,
  type TegelConcept,
  type TegelVersturen,
  type TegelVerstuurd,
  type WachtrijRij,
  type WerkplekKlantGesprek,
  type WerkplekKlantNotitie,
  type WerkplekKlantOrder,
  type WerkplekKlantPagina,
} from "@/lib/werkplek-api";
import type { Diersoort } from "@/types/api";
import type {
  DierWijziging,
  KaartFotoKeuze,
  KaartTijdlijn,
  KlantenCijfers,
  KlantKaart,
  RasKeuze,
} from "./Klanten/types";

/** Eén rij van PUT /api/huisdieren/{id}/allergieen (sessie 73 blok 2). */
export interface DierAllergieRij {
  allergeen: string;
  niveau: "voorkeur" | "intolerantie" | "allergie";
  toelichting?: string | null;
  vastgesteld_door_dierenarts: boolean;
}

/** Body van POST /api/werkplek/klanten/{id}/vragen. */
export interface KlantVraagInvoer {
  veld: string;
  huisdier_id?: string | null;
  /** Leeg: alleen gevraagd. "wil_niet": nooit meer vragen. */
  antwoord?: "gegeven" | "wil_niet" | "geen_antwoord";
  kanaal?: string;
}

export type {
  AfrondUitkomst,
  KoppelBanner,
  TegelBijlage,
  TegelConcept,
  TegelVersturen,
  TegelVerstuurd,
  WachtrijRij,
  WerkplekKlantGesprek,
  WerkplekKlantNotitie,
  WerkplekKlantOrder,
  WerkplekKlantPagina,
};

/**
 * Sessie 75: het verloop per order uit `/klanten/{id}/orders`
 * (`haal_orders`), gelijk aan de laatste bestelling van de klantkaart.
 * Optioneel: live-orders uit Lightspeed hebben geen zending.
 */
export interface V6OrderVerloop {
  verzonden_op?: string | null;
  bezorgd_op?: string | null;
  vervoerder?: string | null;
  partij?: string | null;
}

// ── Gedeelde woorden ─────────────────────────────────────────────────

/** De vier kanalen van de tegels, in de volgorde van de mock. */
export type Kanaal = "mail" | "chat" | "whatsapp" | "telefoon";
export const KANALEN: Kanaal[] = ["mail", "chat", "whatsapp", "telefoon"];
export const KANAAL_WOORD: Record<Kanaal, string> = {
  mail: "E-mail",
  chat: "Chat",
  whatsapp: "WhatsApp",
  telefoon: "Telefoon",
};

/** De vier mappen boven de lijst (Systeem sinds sessie 53). */
export type Map = "open" | "afgerond" | "spam" | "systeem";

/** De mappen in de volgorde van het segment, met hun woord. */
export const MAPPEN: { map: Map; woord: string }[] = [
  { map: "open", woord: "Open" },
  { map: "afgerond", woord: "Afgerond" },
  { map: "spam", woord: "Spam" },
  { map: "systeem", woord: "Systeem" },
];

/**
 * De groep van een kaart. Bepaalt de volgorde (beslis, live, wacht en
 * terugbellen, dan tijd) en de kleur: alleen `beslis` en `live` krijgen
 * de oranje tint, de rest is grijs.
 */
export type Groep =
  | "beslis"
  | "live"
  | "wacht"
  | "terugbellen"
  | "afgerond"
  | "spam"
  | "systeem";

/** Stoplichtstand van een open kaart (sessie 53); groen komt niet op een kaart. */
export type KaartStand = "rood" | "oranje" | "akkoord";

// ── Lijst (nieuw: GET /api/werkplek/v6/lijst) ────────────────────────

/**
 * Het kanaal op een kaart: een van de vier, of `taak` (sessie 72, punt 5)
 * voor een zaak zonder gesprek, met alleen een taak uit het actiecentrum.
 * Een taak heeft geen tegel en geen filter.
 */
export type KaartKanaal = Kanaal | "taak";

export interface V6Kaart {
  zaak_id: string;
  kanaal: KaartKanaal;
  /** Voornaam en achternaam, of het adres als er geen klant is. */
  naam: string;
  /** Moment van het laatste bericht (ISO, UTC). */
  moment: string;
  /** Onderwerp van de mail, anders de vraag in één regel. */
  onderwerp: string;
  groep: Groep;
  /** Het woord op het pilletje: "Beslissen", "Nova antwoordt live",
   *  "Wacht op jou", "Terugbellen na 15:00", "Afgerond door Nova". */
  status_woord: string;
  /** Korte toelichting naast het pilletje: "concept klaar", "25 procent
   *  terug". Leeg als er niets te zeggen is. */
  toelichting: string | null;
  /** Sinds wanneer de klant op ons wacht (ISO), anders null. */
  wacht_sinds: string | null;
  /** Nieuw sinds JP de zaak opende: naam en regel vet. */
  nieuw: boolean;
  klant_id: string | null;
  /** Chatsessie of e-mail-id's van deze zaak, om live-events aan de
   *  kaart te koppelen (`gesprek_id` op de stroom). */
  gesprek_ids: string[];
  /** Alleen bij map afgerond: het moment van afronden (voor de dagkop). */
  afgerond_op: string | null;
  /** Sessie 82: de map waarin deze zaak staat (zoeken gaat over alle mappen). */
  map?: Map;
  /** Sessie 53: stoplichtstand van de handeling op deze kaart (alleen open). */
  stand?: KaartStand | null;
  /** Sessie 53: mag in bulk verwerkt worden (stand akkoord, bericht
   *  compleet, geen huiswerkgat, actie aangesloten). */
  vinkbaar?: boolean;
  /** Motor 7 sessie D: de tegel van een open kaart (server); null buiten Open. */
  tegel?: Tegel | null;
  /** Binnenkomst van de zaak (ISO), zoals de kop van het midden. */
  binnen?: string | null;
  /** Vroegste termijn van een open belofte aan de klant (ISO), anders null. */
  belofte_voor?: string | null;
  /** Redenen uit het overdrachtsblok van motor 7 (geld, toezegging, ...). */
  motor7_redenen?: string[];
}

/** De vier tegels boven de lijst (keuze 1 van 75). */
export type Tegel = "voor_jou" | "nakijken" | "wacht" | "afgerond";

export interface V6Lijst {
  map: Map;
  kaarten: V6Kaart[];
  /** Open zaken per kanaal, altijd over map open en zonder zoekterm.
   *  `taak` telt de taakkaarten apart (sessie 72); samen is het `open`. */
  tellers: Record<Kanaal, number> & { taak?: number };
  /** Aantal open zaken in totaal (bij de map Open). */
  open: number;
  /** Sessie D (alleen map Open): kaarten per tegel plus `beloftes`; `afgerond`
   *  telt ook `afgerond_vandaag` mee. */
  tegels?: Partial<Record<Tegel | "beloftes", number>>;
  afgerond_vandaag?: number;
}

export interface LijstVraag {
  map: Map;
  kanaal?: Kanaal | null;
  zoek?: string | null;
}

// ── Eén zaak (nieuw: GET /api/werkplek/v6/zaken/{id}) ────────────────

/** De vijf standen van het antwoordvak, plus alleen-lezen na afronden. */
export type Stand = "mail" | "chat" | "beslis" | "bel" | "live" | "afgerond";

export interface V6Foto {
  /** Id voor het ophalen als blob, zie `laadFoto`. */
  id: string;
  naam: string;
  /** "chat" of "mail". */
  bron: string;
  email_id: string | null;
  /** Wat Nova op de foto zag, één regel. */
  blik: string | null;
}

/**
 * Een mail zoals de werkplek hem toont (`mail_weergave.mail_voor_werkplek`):
 * in de zaak als `mail` bij een klantmail, in het logboek via
 * `GET /api/werkplek/v6/mails/{id}/weergave` (sessie 186, punt 7).
 */
export interface V6Mail {
  /** Geschoonde opmaak zonder het eerdere bericht; leeg zonder HTML. */
  html: string;
  /** Bevat afbeeldingen van buiten; die laden pas na een klik. */
  extern: boolean;
  /** Het eerdere bericht als opgemaakt kader (`vo-doorgestuurd`). */
  citaat_html?: string;
  /** Platte tekst zonder markeringen en zonder het eerdere bericht. */
  tekst?: string;
  /** Het eerdere bericht als platte tekst. */
  citaat?: string;
  doorgestuurd?: boolean;
}

export interface V6Regel {
  soort: "tegel" | "gegevens" | "kaart" | string;
  data: Record<string, unknown>;
}

export interface V6Bijlage {
  id: string;
  naam: string;
  soort: string | null;
  email_id: string | null;
}

/**
 * Eén regel in het gesprek. `soort` bepaalt de vorm:
 * - `mail`: kaart met afzender, aan, tijd, onderwerp, tekst;
 * - `ballon`: chat, WhatsApp of telefoon; klant links, wij rechts;
 * - `notitie`: dunne gele regel (intern, nooit naar de klant);
 * - `systeem`: dagkop of gebeurtenis, klein en grijs in het midden.
 */
export interface V6Bericht {
  id: string;
  soort: "mail" | "ballon" | "notitie" | "systeem";
  kanaal: Kanaal | null;
  /** "klant" (links) of "wij" (rechts). Leeg bij notitie en systeem. */
  kant: "klant" | "wij" | null;
  /** "Ria de Vries", "Nova", "Sanne", "JP". */
  wie: string;
  moment: string;
  tekst: string;
  /** Mail: afzender met adres, ontvanger(s), onderwerp. */
  van: string | null;
  aan: string | null;
  onderwerp: string | null;
  /** Veilige, gestripte HTML van onze eigen mail (alleen p, br, b, i, a, ul, li). */
  html: string | null;
  /** Klantmail zoals verstuurd (sessie 75): geschoonde HTML voor het veilige vlak. */
  mail?: V6Mail | null;
  /** Sessie 75: wat de klant zag en deed (tegel, gegevens, kaarten). */
  regel?: V6Regel | null;
  /** WhatsApp: de goedgekeurde template waarmee dit bericht uitging. */
  template_naam?: string | null;
  /** WhatsApp, ons bericht: "verzonden", "bezorgd", "gelezen", "mislukt". */
  verzendstatus: string | null;
  /** "tekst", "afbeelding", "spraak", "voicemail", "document". */
  bericht_soort: string | null;
  transcript: string | null;
  /** Duur van spraak of voicemail in seconden. */
  duur_seconden: number | null;
  /** Spraakbericht of voicemail om af te spelen; laad via `laadAudio`. */
  audio: { id: string; bron: "chat" | "mail" | "telefoon" } | null;
  fotos: V6Foto[];
  bijlagen: V6Bijlage[];
  /** Motor sessie 5b: een voorstel van de nieuwe motor dat een bestaande uitvoerder kan doen;
   *  de klik van JP is het besluit. Alleen op een taakregel. */
  knop?: { label: string; pad: string } | null;
}

/** Wat de knop uitvoeren teruggeeft (motor sessie 5b). */
export interface V6HandUitkomst {
  /** "uitgevoerd", "voorstel_jp", "geweigerd" of "proef_niet_uitgevoerd". */
  stand: string;
  reden: string | null;
  wat: string;
}

export interface V6Keuze {
  tekst: string;
  /** "€ 8,74", "gratis label"; leeg mag. */
  detail: string | null;
  advies: boolean;
}

export interface V6Beslis {
  /** Voorstel in één zin ("25 procent terug: € 8,74 als kortingscode").
   *  Null als de motor geen voorstel gaf: dan valt de regel weg. */
  voorstel: string | null;
  /** Uitleg in een of twee zinnen. */
  uitleg: string | null;
  /** Keuzes, advies bovenaan. Leeg als er niets te kiezen is. */
  keuzes: V6Keuze[];
  /** Een voorgestelde actie die met het bericht wordt uitgevoerd. */
  actie: { id: string; label: string } | null;
}

export interface V6ConceptRegel {
  /** "concept klaar", "voorstel: afronden, bedankje" of "geen concept". */
  toelichting: string;
  afronden_label: string | null;
  /** "copiloot", "wekker", "sanne", "nova" of "leeg". */
  bron: string;
}

export interface V6Bel {
  nummer: string | null;
  /** "Na 15:00, klant is dan thuis." */
  tijdvak: string | null;
  gesprek_id: string | null;
  klant_id: string | null;
}

export interface V6Live {
  /** De chatsessie (chat en WhatsApp) voor overnemen en teruggeven. */
  sessie_id: string;
  overgenomen: boolean;
}

/** Wat het klantvlak nodig heeft naast de klantpagina (sessie 67). */
export interface V6Klantkaart {
  /** "e-mailadres", "WhatsApp-nummer", "naam en postcode", ... of null. */
  gekoppeld_op: string | null;
  laatste_bestelling: {
    nummer: string;
    lightspeed_url: string | null;
    besteld_op: string | null;
    /** Sessie 72: uit de zending; null als het niet vastligt (geen gok). */
    verzonden_op: string | null;
    bezorgd_op: string | null;
    /** Wie verzond: "Cenfil" bij een Picqer-order, anders null. */
    partij: string | null;
    bedrag_cents: number;
    regels: { omschrijving: string; aantal: number }[];
    /** Status als woord ("Onderweg", "Bezorgd"). */
    status: string | null;
    vervoerder: string | null;
    track_code: string | null;
    track_url: string | null;
  } | null;
  aantal_bestellingen: number;
  /** Voer dat de klant gebruikt, met wanneer het op is. */
  gebruikt: { product: string; wanneer: string | null }[];
}

/** Eén kanaal in de kanaalkeuze (sessie 70, punt 13). */
export interface V6KanaalStand {
  kanaal: Kanaal;
  /** Kan JP hierlangs antwoorden? */
  aan: boolean;
  /** Waarom niet, in een paar woorden ("geen e-mailadres", "venster dicht"). */
  reden: string | null;
  /** E-mailadres of nummer, als dat bekend is. */
  adres: string | null;
  /** Chat en WhatsApp: de sessie van dit kanaal (bijlagen, recensie 13). */
  sessie_id?: string | null;
  /**
   * Sessie 9U (C18): WhatsApp in een telefoonzaak zonder gesprek. Het nummer
   * van de klant staat klaar; JP begint het gesprek in deze zaak.
   */
  starten?: boolean;
}

/** Uitkomst van "WhatsApp beginnen" in een telefoonzaak (sessie 9U, C18). */
export interface V6WhatsAppInZaak {
  zaak_id: string;
  sessie_id: string;
  gekoppeld: boolean;
  /** Het gesprek van dit nummer loopt al in deze andere open zaak. */
  andere_zaak_id: string | null;
}

export interface V6Zaak {
  zaak_id: string;
  kanaal: Kanaal;
  onderwerp: string;
  groep: Groep;
  status_woord: string;
  /** "via klantenservice@voeronline.nl", "WhatsApp 06 12 34 56 78". */
  bron: string | null;
  wacht_sinds: string | null;
  stand: Stand;
  klant_id: string | null;
  /** Sessie 72 (punt 7): naam van de gekoppelde klant, anders null. */
  klant_naam?: string | null;
  gesprek_ids: string[];
  /** Alleen de berichten van deze zaak (sessie 68, punt 7). */
  berichten: V6Bericht[];
  /** Oudere berichten uit dezelfde chat- of WhatsApp-draad, van vóór deze
   *  zaak. Het midden toont ze achter één inklapregel "Eerdere gesprekken
   *  (n)" boven de berichten. Leeg bij mail en bij een nieuwe draad. */
  eerdere_berichten?: V6Bericht[];
  beslis: V6Beslis | null;
  /** Sessie 68 (punt 3): het concept in één regel, uit dezelfde functie
   *  als de kaart en `/concept`. Null bij live en afgerond. */
  concept?: V6ConceptRegel | null;
  bel: V6Bel | null;
  live: V6Live | null;
  /** Bij stand chat: het kanaal waar het antwoord heen gaat, met de
   *  reden in woorden ("klant is offline, Nova beloofde antwoord per
   *  mail"). `via` is "mail", "chat" of "whatsapp". */
  via: {
    kanaal: Kanaal;
    tekst: string | null;
    kan_wisselen: boolean;
    /** E-mailadres van de klant, voor antwoorden per mail (of null). */
    aan: string | null;
  } | null;
  /** Sessie 70 (punt 13): de vier kanalen voor de kanaalkeuze bovenaan
   *  het antwoordvak, altijd in de volgorde mail, chat, WhatsApp, bellen.
   *  Optioneel zodat een oudere server het vak niet breekt. */
  kanalen?: V6KanaalStand[];
  /** Het kanaal van het laatste klantbericht (anders dat van de zaak). */
  kanaal_standaard?: Kanaal;
  /** Andere open zaken van dezelfde klant (voor samenvoegen). */
  andere_open_zaken: { zaak_id: string; onderwerp: string; kanaal: Kanaal }[];
  klantkaart: V6Klantkaart | null;
  /** TAP-tickets 28-10: navraag bij de vervoerder, nieuwste eerst. Leeg
   *  of afwezig: de kop toont niets. Optioneel voor een oudere server. */
  navraag?: V6Navraag[];
}

/** Eén TAP-ticket bij de vervoerder. `dag` alleen bij een lopend ticket. */
export interface V6Navraag {
  ticket_nummer: string | null;
  status: string | null;
  stand: "Open" | "Bij de vervoerder" | "Gesloten" | "Onbekend";
  dag: number | null;
  url: string;
}

/** Sessie 105 (2d): de zaak plus de berichten van alle zaken van de klant. */
export interface V6Klantgesprek extends V6Zaak {
  klantgesprek: {
    zaken: { zaak_id: string; kanaal: string; onderwerp: string; status_woord: string | null }[];
    afgekapt: boolean;
    /** Zaken van de klant buiten de nieuwste acht (alleen bij afgekapt). */
    niet_opgenomen?: { zaak_id: string; kanaal: string; onderwerp: string }[];
    /** Er zijn zaken zonder gekoppelde klant; die kunnen ontbreken. */
    zonder_klant?: boolean;
  };
}

// ── Controles (nieuw: GET /api/werkplek/v6/zaken/{id}/controles) ─────

export interface V6Controle {
  tekst: string;
  /** "Lightspeed", "Picqer", "DHL", "MultiSafepay", "Portaal", "Kennis", "Motor". */
  bron: string;
  /** "ok", "niet_gevonden" of "twijfel". */
  uitkomst: "ok" | "niet_gevonden" | "twijfel";
}

export interface V6Controles {
  /** "Levering", "Schade, vijf controles", "Account en punten". */
  titel: string;
  items: V6Controle[];
}

// ── Documenten en bijlagen (nieuw) ───────────────────────────────────

export interface V6Document {
  /** "factuur", "pakbon", "retourlabel". */
  soort: string;
  naam: string;
  beschikbaar: boolean;
  /** Waarom niet, kort ("geen retour op deze zaak"). */
  reden_uit: string | null;
}

/** Een eigen bijlage of portaaldocument, klaar om mee te sturen. */
export interface V6UitgaandeBijlage {
  id: string;
  naam: string;
  soort: string;
  grootte_bytes: number;
}

export interface PerMailVraag {
  aan: string[];
  cc?: string[];
  bcc?: string[];
  onderwerp: string;
  tekst: string;
  bijlage_ids?: string[];
  moment?: string;
  /** "sanne" of "jp": wiens handtekening eronder staat. */
  stem?: string;
  /** JP bevestigde een tekst die al verstuurd is. */
  bevestig_dubbel?: boolean;
  /** Beslissen via een ander kanaal: de actie van Nova gaat mee (sessie 70). */
  actie_id?: string | null;
  /** Sessie 75: "Akkoord en versturen" vanuit Beslissen. */
  uit_beslissen?: boolean;
}

/** Antwoord in het chat- of WhatsApp-gesprek van de zaak (sessie 67). */
export interface PerGesprekVraag {
  kanaal: "chat" | "whatsapp";
  tekst: string;
  moment?: string;
  stem?: string;
  bevestig_dubbel?: boolean;
  actie_id?: string | null;
  /** Sessie 75: "Akkoord en versturen" vanuit Beslissen. */
  uit_beslissen?: boolean;
}

export type TerugbelUitkomst = "afgehandeld" | "niet_bereikt" | "later";

export interface TerugbelVraag {
  uitkomst: TerugbelUitkomst;
  notitie?: string | null;
  /** Bevestiging per mail; leeg = geen mail. */
  bevestiging?: { aan: string[]; onderwerp: string; tekst: string } | null;
}

export interface TerugbelAntwoord {
  zaak_id: string;
  uitkomst: TerugbelUitkomst;
  afgerond: boolean;
  mail_klaargezet: boolean;
  ongedaan_tot: string | null;
  /** Het afrond-event bij afgehandeld, voor Ongedaan maken. */
  event_id: string | null;
}

// ── Copiloot (bestaand: /api/werkplek/copiloot/{zaak_id}/...) ────────

export interface CopilootBericht {
  id: string;
  /** "gebruiker" (JP) of "assistent". */
  rol: string;
  inhoud: string;
  /** Een aangepast concept: de copiloot zet dit direct in het antwoordvak. */
  concept_tekst: string | null;
  concept_onderwerp: string | null;
  created_at: string;
  /** Sessie 55: label en pakbon van een retour die de copiloot via de
   *  motor maakte. Het antwoordvak neemt ze over. Afwezig = geen. */
  bijlagen?: V6UitgaandeBijlage[] | null;
}

export interface CopilootDraad {
  zaak_id: string;
  kan_chatten: boolean;
  reden_uit: string | null;
  berichten: CopilootBericht[];
}

export interface CopilootStreamOpties {
  onDelta?: (tekst: string) => void;
  onHerkansing?: () => void;
  onKlaar?: (bericht: CopilootBericht) => void;
  onFout?: (melding: string) => void;
  signal?: AbortSignal;
  /** Wat er nu in het antwoordvak staat (sessie 68): de copiloot past
   *  die tekst aan. Weglaten = de oude vorm zonder vak. */
  huidigConcept?: string;
}

// ── Bulk verwerken (sessie 53: POST /api/werkplek/v6/verwerk) ────────

/** Uitkomst per zaak; een fout bij één zaak stopt de rest niet. */
export interface VerwerkResultaat {
  zaak_id: string;
  ok: boolean;
  melding: string;
}

/** Maximaal aantal zaken per verwerk-aanroep (contract van de server). */
export const VERWERK_MAX = 50;
/** Het scherm stuurt kleinere porties: elke zaak verstuurt echt, dus een
 *  portie van 50 loopt langer dan een gewone aanroep mag duren. */
export const VERWERK_PORTIE = 10;
/** Eigen, ruime timeout per portie. */
export const VERWERK_TIMEOUT_MS = 120_000;

// ── De functies ──────────────────────────────────────────────────────

export const v6Api = {
  /** Motor sessie 5b: de knop uitvoeren op een voorstel van de nieuwe motor. */
  async handUitvoeren(pad: string): Promise<V6HandUitkomst> {
    const { data } = await apiClient.post<V6HandUitkomst>(pad);
    return data;
  },
  // Lijst en zaak (nieuw)
  async lijst(vraag: LijstVraag): Promise<V6Lijst> {
    const { data } = await apiClient.get<V6Lijst>("/api/werkplek/v6/lijst", {
      params: {
        map: vraag.map,
        kanaal: vraag.kanaal ?? undefined,
        zoek: vraag.zoek?.trim() || undefined,
      },
    });
    return data;
  },
  async zaak(zaakId: string): Promise<V6Zaak> {
    const { data } = await apiClient.get<V6Zaak>(`/api/werkplek/v6/zaken/${zaakId}`);
    return data;
  },
  /** Sessie 186 (punt 7): één mail zoals de werkplek hem toont, voor het logboek. */
  async mailWeergave(emailId: string): Promise<V6Mail> {
    const { data } = await apiClient.get<V6Mail>(`/api/werkplek/v6/mails/${emailId}/weergave`);
    return data;
  },
  /** Sessie 105 (2d): het hele gesprek van de klant over al zijn zaken en kanalen. */
  async klantgesprek(zaakId: string): Promise<V6Klantgesprek> {
    const { data } = await apiClient.get<V6Klantgesprek>(
      `/api/werkplek/v6/zaken/${zaakId}/klantgesprek`,
    );
    return data;
  },
  async controles(zaakId: string): Promise<V6Controles> {
    const { data } = await apiClient.get<V6Controles>(
      `/api/werkplek/v6/zaken/${zaakId}/controles`,
    );
    return data;
  },
  /** Zaak bij een chat- of mail-id uit een pushmelding (`/?chat=`, `/?mail=`). */
  zaakVanBron: (soort: "chat" | "mail", id: string) => kaartenApi.zaakVanBron(soort, id),

  // Antwoordvak (bestaand)
  concept: (zaakId: string) => werkplekTegelApi.concept(zaakId),
  versturen: (zaakId: string, vraag: TegelVersturen) => werkplekTegelApi.versturen(zaakId, vraag),
  intrekken: (wachtrijId: string) => werkplekTegelApi.ongedaan(wachtrijId),
  /** Sessie 72: de stand van de verzendingen van een zaak (wacht, verstuurd, mislukt). */
  wachtrij: (zaakId: string) => werkplekTegelApi.wachtrij(zaakId),
  notitie: (zaakId: string, tekst: string) => werkplekTegelApi.notitie(zaakId, tekst),
  bellen: (invoer: { nummer: string; klant_id?: string | null; zaak_id?: string | null }) =>
    werkplekTegelApi.bellen(invoer),

  /** Sessie 9U (C18): WhatsApp met de klant beginnen in deze zaak. Verstuurt niets. */
  async whatsappInZaak(zaakId: string): Promise<V6WhatsAppInZaak> {
    const { data } = await apiClient.post<V6WhatsAppInZaak>(
      `/api/werkplek/v6/zaken/${zaakId}/whatsapp`,
    );
    return data;
  },
  /** Antwoord per mail op een chat-, WhatsApp- of telefoonzaak (nieuw). */
  async perMail(zaakId: string, vraag: PerMailVraag): Promise<TegelVerstuurd> {
    const { data } = await apiClient.post<TegelVerstuurd>(
      `/api/werkplek/v6/zaken/${zaakId}/per-mail`,
      vraag,
    );
    return data;
  },
  /**
   * Antwoord in het chat- of WhatsApp-gesprek (nieuw). Gebruik dit in stand
   * chat in plaats van `versturen`: liep de zaak eerder per mail, dan maakt
   * dit het gesprek weer de actieve conversatie.
   */
  async perGesprek(zaakId: string, vraag: PerGesprekVraag): Promise<TegelVerstuurd> {
    const { data } = await apiClient.post<TegelVerstuurd>(
      `/api/werkplek/v6/zaken/${zaakId}/per-gesprek`,
      vraag,
    );
    return data;
  },
  /** Uitkomst van een terugbelverzoek, met notitie en bevestigingsmail (nieuw). */
  async terugbellen(zaakId: string, vraag: TerugbelVraag): Promise<TerugbelAntwoord> {
    const { data } = await apiClient.post<TerugbelAntwoord>(
      `/api/werkplek/v6/zaken/${zaakId}/terugbellen`,
      vraag,
    );
    return data;
  },

  /** WhatsApp: een bestand direct sturen (PDF, JPG, PNG; sessie 52). */
  async stuurWhatsappBestand(sessieId: string, bestand: File, bijschrift?: string): Promise<void> {
    const form = new FormData();
    form.append("bestand", bestand);
    if (bijschrift) form.append("bijschrift", bijschrift);
    await apiClient.post(`/api/admin/chat/sessies/${sessieId}/media`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Bijlagen en documenten (nieuw; alleen voor mail)
  async uploadBijlage(zaakId: string, bestand: File): Promise<V6UitgaandeBijlage> {
    const form = new FormData();
    form.append("bestand", bestand);
    const { data } = await apiClient.post<V6UitgaandeBijlage>(
      `/api/werkplek/v6/zaken/${zaakId}/bijlagen`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  async documenten(zaakId: string): Promise<V6Document[]> {
    const { data } = await apiClient.get<V6Document[]>(
      `/api/werkplek/v6/zaken/${zaakId}/documenten`,
    );
    return data;
  },
  async documentAlsBijlage(zaakId: string, soort: string): Promise<V6UitgaandeBijlage> {
    const { data } = await apiClient.post<V6UitgaandeBijlage>(
      `/api/werkplek/v6/zaken/${zaakId}/documenten/${soort}`,
    );
    return data;
  },

  // Sluiten, spam, verwijderen: de bestaande afrondservice. Er wordt
  // niets gewist; verwijderen is een reden, de zaak blijft vindbaar via
  // Zoek op alles (map Afgerond) en terug te zetten met ongedaan.
  sluiten: (zaakId: string) => afrondenApi.rondAf(zaakId, "verhaal_rond"),
  /** Afronden met de reden die Sanne of Nova voorstelde (sessie 68). */
  rondAf: (zaakId: string, reden: string) => afrondenApi.rondAf(zaakId, reden),
  spam: (zaakId: string) => afrondenApi.rondAf(zaakId, "spam"),
  verwijderen: (zaakId: string) => afrondenApi.rondAf(zaakId, "verwijderd"),
  ongedaan: (zaakId: string, eventId: string) => afrondenApi.ongedaan(zaakId, eventId),

  // Live: overnemen en teruggeven (bestaande overname van sessie 52)
  overnemen: (sessieId: string) => adminChatApi.neemOver(sessieId),
  teruggeven: (sessieId: string) => adminChatApi.geefTerug(sessieId),

  // Samenvoegen (nieuw): deze zaak gaat op in een andere open zaak.
  async samenvoegen(zaakId: string, inZaakId: string): Promise<{ zaak_id: string }> {
    const { data } = await apiClient.post<{ zaak_id: string }>(
      `/api/werkplek/v6/zaken/${zaakId}/samenvoegen`,
      { in_zaak_id: inZaakId },
    );
    return data;
  },

  // Klantkaart v6 (sessie 73): cijferbalk, kaart en tijdlijn.
  async klantenCijfers(): Promise<KlantenCijfers> {
    const { data } = await apiClient.get<KlantenCijfers>("/api/werkplek/klanten/cijfers");
    return data;
  },
  /** `bron` is alleen voor de meting (`klantkaart_geopend`): pagina of zaak. */
  async klantKaart(klantId: string, bron: "pagina" | "zaak" = "pagina"): Promise<KlantKaart> {
    const { data } = await apiClient.get<KlantKaart>(`/api/werkplek/klanten/${klantId}/kaart`, {
      params: { bron },
    });
    return data;
  },
  /** `voor`: cursor uit `volgende_voor` om oudere regels te lezen (sessie 73 blok 3). */
  async klantKaartTijdlijn(klantId: string, limiet = 100, voor?: string | null): Promise<KaartTijdlijn> {
    const { data } = await apiClient.get<KaartTijdlijn>(
      `/api/werkplek/klanten/${klantId}/kaart/tijdlijn`,
      { params: voor ? { limiet, voor } : { limiet } },
    );
    return data;
  },
  // Sessie 73 blok 3: registers (toestemming, samenvoeging) en koppelen.
  async klantToestemming(
    klantId: string,
    soort: "seintje" | "nieuwsbrief",
    body: { stand: "ja" | "nee"; kanaal?: string | null },
  ): Promise<void> {
    await apiClient.put(`/api/werkplek/klanten/${klantId}/toestemmingen/${soort}`, body);
  },
  async klantSamenvoegen(
    klantId: string,
    body: { bij_klant_id: string; status: "samengevoegd" | "niet_deze_klant"; reden?: string | null },
  ): Promise<void> {
    await apiClient.post(`/api/werkplek/klanten/${klantId}/samenvoeging`, body);
  },
  async klantSamenvoegingOngedaan(klantId: string, bijKlantId: string): Promise<void> {
    await apiClient.delete(`/api/werkplek/klanten/${klantId}/samenvoeging/${bijKlantId}`);
  },
  /** Zaak aan een klant koppelen, ook een telefoonzaak zonder mail (belstand). */
  async zaakKlantKoppelen(zaakId: string, klantId: string): Promise<{ zaak_id: string; klant_id: string }> {
    const { data } = await apiClient.post<{ zaak_id: string; klant_id: string }>(
      `/api/werkplek/v6/zaken/${zaakId}/klant`,
      { klant_id: klantId },
    );
    return data;
  },
  klantenLijst: (zoek?: string) => werkplekKlantenApi.lijst(zoek),

  // Sessie 73 blok 2: dier bewerken, foto, dossier en vragen.
  /** Wijzig een dier; `wissen` maakt velden leeg. */
  async huisdierWijzig(huisdierId: string, body: DierWijziging): Promise<unknown> {
    const { data } = await apiClient.patch(`/api/huisdieren/${huisdierId}`, body);
    return data;
  },
  /** Sessie 141-E: een voorstel uit een gesprek overnemen of laten (één klik). */
  async huisdierVoorstel(
    huisdierId: string,
    sleutel: string,
    actie: "overnemen" | "laten",
  ): Promise<unknown> {
    const { data } = await apiClient.post(`/api/huisdieren/${huisdierId}/voorstel`, {
      sleutel,
      actie,
    });
    return data;
  },
  /** Vervang de lijst om te vermijden (alles in één keer). */
  async huisdierAllergieen(huisdierId: string, rijen: DierAllergieRij[]): Promise<unknown> {
    const { data } = await apiClient.put(`/api/huisdieren/${huisdierId}/allergieen`, {
      allergieen: rijen,
    });
    return data;
  },
  /** Rassen voor de keuzelijst, uit de raskaarten. */
  async rassen(diersoort: string): Promise<RasKeuze[]> {
    const { data } = await apiClient.get<RasKeuze[]>("/api/werkplek/rassen", {
      params: { diersoort },
    });
    return data;
  },
  /** Foto's uit chat en WhatsApp van deze klant. */
  async klantFotos(klantId: string): Promise<KaartFotoKeuze[]> {
    const { data } = await apiClient.get<KaartFotoKeuze[]>(
      `/api/werkplek/klanten/${klantId}/fotos`,
    );
    return data;
  },
  /** Zet een foto uit een gesprek als foto van het dier (verwijzing, geen kopie). */
  async dierFotoZet(klantId: string, huisdierId: string, berichtId: string): Promise<unknown> {
    const { data } = await apiClient.put(
      `/api/werkplek/klanten/${klantId}/dieren/${huisdierId}/foto`,
      { bericht_id: berichtId },
    );
    return data;
  },
  /** Haal de verwijzing naar de foto weg; de foto zelf blijft in het gesprek. */
  async dierFotoWeg(klantId: string, huisdierId: string): Promise<void> {
    await apiClient.delete(`/api/werkplek/klanten/${klantId}/dieren/${huisdierId}/foto`);
  },
  /** Vragenregister: gevraagd (zonder antwoord) of "wil niet delen". */
  async klantVraag(klantId: string, body: KlantVraagInvoer): Promise<unknown> {
    const { data } = await apiClient.post(`/api/werkplek/klanten/${klantId}/vragen`, body);
    return data;
  },
  /** Nieuw dier met naam en soort (bestaande route); de rest via bewerken. */
  nieuwDier: (klantId: string, invoer: { naam: string; diersoort: Diersoort }) =>
    huisdierenApi.maak(klantId, invoer),
  /** Een afbeelding achter het token (foto van een dier), als blob. */
  async laadAfbeelding(url: string): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(url, { responseType: "blob" });
    return data;
  },

  // Klant (bestaand)
  klant: (klantId: string) => werkplekKlantenApi.pagina(klantId),
  klantOrders: (klantId: string) => werkplekKlantenApi.orders(klantId),
  klantGesprekken: (klantId: string) => werkplekKlantenApi.gesprekken(klantId),
  klantNotities: (klantId: string) => werkplekKlantenApi.notities(klantId),
  koppeling: (zaakId: string) => werkplekKoppelingApi.banner(zaakId),
  nietDezeKlant: (zaakId: string, voorstelId: string) =>
    werkplekKoppelingApi.negeer(zaakId, voorstelId),
  klantLosmaken: (zaakId: string) => werkplekKoppelingApi.maakKlantLos(zaakId),

  // Instellingen die het scherm leest (sleutel `copiloot_alleen_afwijkingen`).
  // Sessie 72: via de route voor iedere medewerker; schrijven blijft admin.
  instellingen: () => werkplekApi.schermInstellingen(),

  // Nieuw contact (sessie 72, punt 10): dezelfde route als altijd.
  /** Zet een zaak klaar voor een nieuw bericht; er gaat niets weg. */
  nieuwBericht: (invoer: {
    klant_id: string;
    kanaal: "mail" | "whatsapp";
    bron: "vandaag" | "klantblok" | "klantpagina";
  }) =>
    kaartenApi.nieuwBericht({
      ...invoer,
      stem: invoer.kanaal === "whatsapp" ? "nova" : "sanne",
    }),
  /** Klanten zoeken op naam, e-mail, telefoon, postcode of ordernummer. */
  zoekKlanten: (zoek: string) => werkplekKlantenApi.lijst(zoek),
  /** WhatsApp buiten het 24-uursvenster: alleen goedgekeurde templates,
   *  over de bestaande verzendweg (`/api/admin/whatsapp-templates/verstuur`,
   *  met het gesprek van de zaak). */
  whatsappTemplates: () => whatsappTemplatesApi.lijst(),
  verstuurTemplate: (invoer: { template_id: string; variabelen: string[]; sessie_id: string }) =>
    whatsappTemplatesApi.verstuur(invoer),

  // Foto's en mailbijlagen vragen een token: laad ze als blob.
  laadFoto(foto: V6Foto): Promise<Blob> {
    if (foto.bron === "mail" && foto.email_id) {
      return emailsApi.bijlageInhoud(foto.email_id, foto.id);
    }
    return adminChatApi.bijlage(foto.id);
  },

  /** Spraak (WhatsApp) als blob; voicemail heeft geen opname in het portaal. */
  laadAudio(audio: NonNullable<V6Bericht["audio"]>): Promise<Blob> {
    return adminChatApi.bijlage(audio.id);
  },

  /** Bulk: verwerk de aangevinkte zaken met dezelfde keten als
   *  "Akkoord en versturen" (sessie 53). Hoogstens VERWERK_MAX per keer;
   *  het scherm stuurt porties van VERWERK_PORTIE met een eigen timeout. */
  async verwerk(zaakIds: string[]): Promise<VerwerkResultaat[]> {
    const { data } = await apiClient.post<{ resultaten: VerwerkResultaat[] }>(
      "/api/werkplek/v6/verwerk",
      { zaak_ids: zaakIds },
      { timeout: VERWERK_TIMEOUT_MS },
    );
    return data.resultaten ?? [];
  },

  // Copiloot (bestaand)
  async copilootDraad(zaakId: string): Promise<CopilootDraad> {
    const { data } = await apiClient.get<CopilootDraad>(
      `/api/werkplek/copiloot/${zaakId}/berichten`,
    );
    return data;
  },
};

/** Sleutel van de Beheer-instelling "Copiloot toont alleen afwijkingen". */
export const INSTELLING_ALLEEN_AFWIJKINGEN = "copiloot_alleen_afwijkingen";

/**
 * Eén copilootbeurt op een zaak, streamend (SSE over POST). Kale `fetch`
 * omdat `EventSource` geen Authorization-header en geen body kan sturen.
 * Events: `delta`, `herkansing`, `klaar` (met het bericht, inclusief een
 * eventueel `concept_tekst`), `fout`.
 */
export async function copilootVraag(
  zaakId: string,
  tekst: string,
  opties: CopilootStreamOpties = {},
): Promise<void> {
  // Eerst een gewone aanroep via apiClient: die ververst een verlopen
  // token, zodat de stream niet op een 401 strandt.
  await apiClient.get(`/api/werkplek/copiloot/${zaakId}/berichten`).catch(() => undefined);
  const token = huidigAccessToken();
  let antwoord: Response;
  try {
    antwoord = await fetch(`/api/werkplek/copiloot/${zaakId}/bericht`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(
        opties.huidigConcept === undefined
          ? { tekst }
          : { tekst, huidig_concept: opties.huidigConcept },
      ),
      signal: opties.signal,
    });
  } catch {
    opties.onFout?.("De copiloot is niet bereikbaar. Probeer het zo opnieuw.");
    return;
  }
  if (!antwoord.ok || !antwoord.body) {
    let melding = "De copiloot kon niet antwoorden. Probeer het zo opnieuw.";
    try {
      const fout = (await antwoord.json()) as { detail?: unknown };
      if (typeof fout.detail === "string") melding = fout.detail;
    } catch {
      /* geen JSON */
    }
    opties.onFout?.(melding);
    return;
  }
  await leesSse(antwoord.body, (data) => {
    const soort = data.type;
    if (soort === "delta" && typeof data.tekst === "string") opties.onDelta?.(data.tekst);
    else if (soort === "herkansing") opties.onHerkansing?.();
    else if (soort === "klaar" && data.bericht) {
      const bericht = data.bericht as CopilootBericht;
      // Sessie 55: `bijlagen` staat naast het bericht in het klaar-event
      // (of in het bericht zelf); het venster leest ze van het bericht.
      const bijlagen = alsBijlagen(data.bijlagen) ?? alsBijlagen(bericht.bijlagen);
      opties.onKlaar?.(bijlagen ? { ...bericht, bijlagen } : bericht);
    }
    else if (soort === "fout")
      opties.onFout?.(
        typeof data.melding === "string"
          ? data.melding
          : "De copiloot kon niet antwoorden. Probeer het zo opnieuw.",
      );
  });
}

/** Alleen een niet-lege lijst bijlagen met een id telt. */
function alsBijlagen(waarde: unknown): V6UitgaandeBijlage[] | null {
  if (!Array.isArray(waarde)) return null;
  const lijst = waarde.filter(
    (b): b is V6UitgaandeBijlage =>
      typeof b === "object" && b !== null && typeof (b as { id?: unknown }).id === "string",
  );
  return lijst.length ? lijst : null;
}

/**
 * Lees een SSE-body regel voor regel en geef elke `data:`-JSON door.
 * `event:`-regels worden meegegeven als `_event`.
 */
export async function leesSse(
  body: ReadableStream<Uint8Array>,
  opData: (data: Record<string, unknown>) => void,
  opCommentaar?: (tekst: string) => void,
): Promise<void> {
  const lezer = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let event: string | null = null;
  for (;;) {
    const { value, done } = await lezer.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let einde = buffer.indexOf("\n");
    while (einde !== -1) {
      const regel = buffer.slice(0, einde).replace(/\r$/, "");
      buffer = buffer.slice(einde + 1);
      if (regel.startsWith(":")) {
        opCommentaar?.(regel.slice(1).trim());
      } else if (regel.startsWith("event:")) {
        event = regel.slice(6).trim();
      } else if (regel.startsWith("data:")) {
        try {
          const data = JSON.parse(regel.slice(5).trim()) as Record<string, unknown>;
          if (event) data._event = event;
          opData(data);
        } catch {
          /* halve of kapotte regel: overslaan */
        }
      } else if (regel === "") {
        event = null;
      }
      einde = buffer.indexOf("\n");
    }
  }
}
